import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

import { ButtonRendererComponent } from "../../ag-grid/button-renderer.component";
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';
import { CheckboxRendererComponent } from "../../ag-grid/checkbox-renderer.component";

import { SwitchRendererComponent } from '../../ag-grid/switch-renderer.component';
import { RouterLinkRendererComponent } from '../../ag-grid/router-link-renderer.component';
import { Organization } from '../../../models/organization.model';
import { ContactService } from '../../../../admin/services/contacts.service';
import { LoaderService } from '../../../services/loader.service';
import { AgentType, Contact, AgentTypeOptions, LegacyContact, LkpLegacyRepresentativeType } from '../../../../admin/models/contact.model';
import { Observable } from 'rxjs';
import { tap } from "rxjs/operators";
import { GridFormatterService } from '../../../services/grid-formatter.service';
import { IBooleanFilterParams, AgGridBooleanFilterComponent } from '../../ag-grid/filters/boolean-filter.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { IconActionRendererComponent } from "../../ag-grid/icon-action-renderer.component";

import { DynamicDialogService } from '../../../../core/services/dynamic-dialog.service';
import { Dialog, DialogButton } from '../../../../core/models/dialog.model';

@Component({
    selector: 'organization-contacts',
    templateUrl: './organization-contacts.component.html',
    styleUrls: ['./organization-contacts.component.scss'],
    standalone: false
})
export class OrganizationContactsComponent implements OnInit {

    public _booleanFilterParams = { isInServerSideFilterMode: false } as IBooleanFilterParams;
    public _isEnabledBooleanFilterParams = { isInServerSideFilterMode: false } as IBooleanFilterParams;


	private _contactData: Contact[] = [];

	@Output() contactSelected: EventEmitter<Contact> = new EventEmitter<Contact>();
	@Input() enableExternalOrgEdit: boolean = false;

	columnEdit = {
		headerName: "",
		field: "",
		cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
		cellRenderer: IconActionRendererComponent,
		cellRendererParams: {
			buttons: [
				{
					action: (obj: any) => this.editContact(obj),
					label: 'Edit Contact',
					icon: 'edit',
					isVisible: (obj: any) => {
						return true;
					}
				}
			]
		},
        filter: false,
		maxWidth: 100
	} as ColDef;

    columnLogin = {
        headerName: "Is Enabled",
        headerTooltip: "Is Enabled",
        field: "canLogIn",
        tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("canLogIn"),
        valueFormatter: this.gridFormatterService.formatBooleanToYesNo("canLogIn"),
        filter: AgGridBooleanFilterComponent,
        filterParams: this._isEnabledBooleanFilterParams,
        colId: "canLogIn",
        sortingOrder: ["desc", "asc", null],
        sort: "desc",
        minWidth: 100,
        cellRenderer: SwitchRendererComponent,
        cellRendererParams: {
            action: this.toggleLogin(this),
            description: "User can log in",
            field: "canLogIn",
            isVisibleField: "isUser"
        },
    } as ColDef;

    columnType = {
        headerName: 'Agent Type',
        headerTooltip: 'Agent Type',
        field: 'agentTypes',
        colId: 'agentTypes',
        valueGetter: this.typeFormatter(this),
        tooltipValueGetter: this.typeFormatter(this),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
    } as ColDef;

    columnNameAsUrl = {
        field: 'displayName',
        headerName: 'Contact Name',
        headerTooltip: 'Contact Name',
        tooltipField: 'displayName',
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            url: '/admin/contact',
            idField: 'id',
            textField: 'displayName'
        },
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
    } as ColDef;

    columnUser = {
        headerName: 'User',
        headerTooltip: 'User',
        field: 'isUser',
        tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("isUser"),
        cellRenderer: CheckboxRendererComponent,
        cellRendererParams: {
                    action: () => { return false; },
                    field: "isUser",
                    description: "User",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
        filter: AgGridBooleanFilterComponent,
        filterParams: this._booleanFilterParams,
        maxWidth: 100
    } as ColDef;

    //Legacy Contact columns
    columnAgentTypeDesc = {
        headerName: 'Agent Type',
        headerTooltip: 'Agent Type',
        colId: "agentType",
        field: 'legacyRepresentativeTypeId',
        tooltipField: 'legacyRepresentativeTypeId',
        filter: "agTextColumnFilter",
        valueFormatter: (params) => this.legacyRepTypeFormatter(params.value), //use a formatter that gets the friendly name from the lookup table
        filterParams: this.gridFormatterService.defaultTextFilterParams, //use a formatter that gets the friendly name from the lookup table
        maxWidth: 125
    } as ColDef;

    columnRepName = {
        headerName: 'Contact Name',
        headerTooltip: 'Contact Name',
        field: 'fullName',
        tooltipField: 'fullName',
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
    } as ColDef;

    columnPhoneNumber = {
        headerName: 'Phone Number',
        headerTooltip: 'Phone Number',
        field: 'phone',
        tooltipField: 'phone',
        valueFormatter: (params) => this.phoneFormatter(params.value),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        maxWidth: 125
    } as ColDef;

    columnExt = {
        headerName: 'Ext',
        headerTooltip: 'Extension',
        field: 'extension',
        tooltipField: 'extension',
        valueFormatter: (params) => this.phoneFormatter(params.value),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 100,
        maxWidth: 100
    } as ColDef;

    columnFaxNumber = {
        headerName: 'Fax Number',
        headerTooltip: 'Fax Number',
        field: 'fax',
        tooltipField: 'fax',
        valueFormatter: (params) => this.phoneFormatter(params.value),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        maxWidth: 150
    } as ColDef;

    columnAltPhoneNumber = {
        headerName: 'Alt Phone Number',
        headerTooltip: 'Alternate Phone Number',
        field: 'alternativePhone',
        tooltipField: 'alternativePhone',
        valueFormatter: (params) => this.phoneFormatter(params.value),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        maxWidth: 150
    } as ColDef;

    columnEmail = {
        headerName: 'Email',
        headerTooltip: 'Email Address',
        field: 'email',
        tooltipField: 'email',
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,

    } as ColDef;

    columnChangeAgentType = {
        headerName: 'Change Agent Type',
        headerTooltip: 'Change Agent Type',
        field: 'changeAgentType',
        cellRenderer: ButtonRendererComponent,
        cellRendererParams: {
            buttonText: "Change",
            action: (obj: any) => this.openChangeAgentTypeModal(obj)
        },  
        filter: false,
        sortable: false,
        maxWidth: 150
    } as ColDef;

    confirmUpdateDialog: Dialog = new Dialog("Confirm change of agent type?", [new DialogButton("Yes", true, () => { this.confirmUpdate(); }), new DialogButton("No", false, () => { return false; })])
    onSuccessDialog: Dialog = new Dialog("Agent type updated successfully.", [new DialogButton("OK", true, () => { this.onSuccessDialog.toggleDialog(false); this.refreshData(); })])

    contactEditableGridColumns = [this.columnNameAsUrl, this.columnType, this.columnUser, this.columnLogin];
    legacyContactGridColumns = [this.columnRepName, this.columnAgentTypeDesc, this.columnPhoneNumber, this.columnExt, this.columnFaxNumber, this.columnAltPhoneNumber, this.columnEmail, this.columnChangeAgentType];
	gridOptions: GridOptions;
	gridApi?: GridApi;
    contacts: Observable<any[]>;
    legacyContacts: Observable<LegacyContact[]>;
    agentTypes: LkpLegacyRepresentativeType[];
    showModal: boolean = false;
    selectedContact: LegacyContact;

    public updateAgentTypeForm: FormGroup;

    @Input() organization: Organization;
    @Input() canEditContacts: boolean;
    @Input() useLegacyContacts: boolean;

    constructor(public service: ContactService, public loader: LoaderService, public gridFormatterService: GridFormatterService, private fb: FormBuilder, public dialogService: DynamicDialogService) {
    }

    ngOnInit() {
        if (this.useLegacyContacts == true)
        {

            this.contacts = this.service.getLegacyContacts(this.organization.id, this.organization.historicalOwnerNumber);
      
            this.contacts.subscribe();

            this.service.getLegacyRepresentativeTypes().subscribe((data) => {

                this.agentTypes = data;
            })

            this.updateAgentTypeForm = this.fb.group(
                {
                    name: [''],
                    agentType: ['']
                }
            );    
            this.gridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.legacyContactGridColumns);
        }
        else
        {

			this.contacts = this.service.getContactsByOrganization(this.organization.id).pipe(
				tap((data: Contact[]) => {
					this._contactData = data;
				})
			)
			if (this.enableExternalOrgEdit) {
				this.contactEditableGridColumns.unshift(this.columnEdit);
			}
			this.gridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.contactEditableGridColumns);
		}
		this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
            this.gridApi.sizeColumnsToFit();
		};
        this.gridOptions.onFirstDataRendered = () => {
            if (this.gridApi != null) {
                this.gridApi.setFilterModel({ agentTypes: { type: 'notContains', filter: 'None' } });
                this.gridApi.onFilterChanged();
            }
		};

		this.gridOptions.onRowDataUpdated = () => {
			if (this.gridApi != null) {
                this.gridApi.setFilterModel({ agentTypes: { type: 'notContains', filter: 'None' } });
			    this.gridApi.onFilterChanged();
            }
		}
	}

	editContact(obj: any): void {
		this.contactSelected.emit(obj);
	}

    toggleLogin(component: any) {
        return (obj: any) => {
            component.loader.toggleLoader(true);
            this.service.setCanLogIn(obj.id, !obj.canLogIn).subscribe(() => {
                obj.canLogIn = !obj.canLogIn;
                component.loader.toggleLoader(false);
            })
        }
    }

    openChangeAgentTypeModal(data: any) {
        this.showModal = true;
 
        this.updateAgentTypeForm.get('name').setValue(data.fullName);
        this.updateAgentTypeForm.get('agentType').setValue(data.legacyRepresentativeTypeId);
        this.selectedContact = data;

    }

    changeAgentType() {

       this.confirmUpdateDialog.toggleDialog(true);
       this.dialogService.updateDialog(this.confirmUpdateDialog);
     
    }

    confirmUpdate() {

        this.selectedContact.legacyRepresentativeTypeId = this.updateAgentTypeForm.get('agentType').value
        
        this.loader.toggleLoader(true);
        this.service.updateLegacyAgentType(this.selectedContact).subscribe((data) => {

            //loader off
            this.loader.toggleLoader(false);
            
            //successful dialog
            this.onSuccessDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.onSuccessDialog);     

        })
        this.showModal = false;


    }

    cancel() {
        this.showModal = false;
    }   

    refreshData() {
        this.contacts = this.service.getLegacyContacts(this.organization.id, this.organization.historicalOwnerNumber);
        this.loader.toggleLoader(true);
        this.contacts.subscribe((data) => { this.loader.toggleLoader(false); });
    }

    typeFormatter(component: any) {
        return (obj: any) => {
            let contactTypes: AgentType = obj.data.agentType;
            let types: AgentType[] = [];
            let displayStrings: string[] = [];

            for (let prop in AgentType)
                if (typeof AgentType[prop] === 'number' && contactTypes & (<any>AgentType[prop]))
                    types.push(<any>AgentType[prop]);

            types.forEach((x) => {
                let description: string = '';
                switch (x) {
                    case AgentType.AuthorizedAgent:
                        description += AgentTypeOptions.AuthorizedAgent.text;
                        break;
                    case AgentType.DesignatedRep:
                        description += AgentTypeOptions.DesignatedRepresentative.text;
                        break;
                    case AgentType.EmergencyPOC:
                        description += AgentTypeOptions.EmergencyPOC.text;
                        break;
                    case AgentType.ExecutiveAuthority:
                        description += AgentTypeOptions.ExecutiveAuthority.text;
                        break;
                    case AgentType.PrimaryContact:
                        description += AgentTypeOptions.PrimaryContact.text;
                        break;
                    case AgentType.StatutoryAgent:
                        description += AgentTypeOptions.StatutoryAgent.text;
                        break;
                    default:
                        description += AgentType[x];
                        break;
                }
                displayStrings.push(description);
            });

            //If we have no entries add in the empty flag value
            if (displayStrings.length == 0)
                displayStrings.push(AgentTypeOptions.None.text);

            return displayStrings.join(', ');
        }
    }

    phoneFormatter(phone: string) {
        if (!phone) return '';
		if (phone.length === 10) return phone.substring(0, 3) + '-' + phone.substring(3, 3) + '-' + phone.substring(6, 4);
        return phone;
    }

    legacyRepTypeFormatter(legacyRepTypeId: number): string {
        let type;
        if (this.agentTypes != null && this.agentTypes.length !== 0) {
			type = this.agentTypes.find(t => t.id === legacyRepTypeId);
        }
        return (type != null && type.value != null) ? type.value as string : 'Unknown Type';

	}

	updateContacts(contacts: Contact[]): void {
		if (this.useLegacyContacts == false) {
            if (this.gridApi != null) {
                this.gridApi.setGridOption("loading", true);
            }
			for (let c = 0; c < contacts.length; c++) {
				let contact = contacts[c];
				let index = this._contactData.findIndex(c => c.id == contact.id);
				if (index != -1) {
					this._contactData.splice(index, 1, contact);
				}
			}
            if (this.gridApi != null) {
                this.gridApi.setGridOption("rowData", this._contactData);
			    setTimeout(() => {
                    if (this.gridApi != null) this.gridApi.setGridOption("loading", false);
			    }, 500);            
            }
			
		}
	}
}
